"use client";

import React from "react";
import dayjs from "dayjs";
import { useAtomValue, useSetAtom } from "jotai";
import { useForm } from "react-hook-form";

import { Form } from "@/components/ui/form";

import { getCoursePackages } from "@/fetchers/course";

import { isAuthenticatedAtom } from "@/store/auth";
import { coursePackagesAtom, userCoursePurchasedAtom } from "@/store/course";

import useMoengageEventTracker from "@/hooks/use-moengage";
import EVENT_NAME from "@/hooks/use-moengage/event-name";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute";

import { addRecentlyViewedCourse } from "@/lib/utils";

function CourseForm({
  course,
  isOnlineEnabled,
  isOfflineEnabled,
  defaultOfflineCentreId,
  children,
}) {
  const trackMoengageEvent = useMoengageEventTracker();

  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const userCoursePurchased = useAtomValue(userCoursePurchasedAtom);

  const methods = useForm({
    defaultValues: {
      courseId: course.courseId,
      parentCourseId: course.parentCourseId,
      languageId: course.defaultLanguageId,
      courseTypeId: course.courseTypeId,
      packageId: null,
      isOnlineEnabled,
      isOfflineEnabled,
      offlineCentreId: defaultOfflineCentreId || undefined,
      mode: isOnlineEnabled ? "online" : "offline",
      action: 1,
    },
    disabled: !!userCoursePurchased,
  });

  const courseMode = methods.watch("mode");
  const courseLanguageId = methods.watch("languageId");
  const offlineCentreId = methods.watch("offlineCentreId");

  const setCoursePackages = useSetAtom(coursePackagesAtom);

  React.useEffect(() => {
    addRecentlyViewedCourse({
      courseId: course.courseId,
      title: course.courseTitle,
      thumbnailUrl: course.thumbnail,
      basePrice: course.courseBasePrice,
      discountPrice: course.packageDiscountPrice2,
      isCourseFree: course.isCourseFree,
      slug: course.courseSlug,
    });

    if (isAuthenticated === null) return;

    trackMoengageEvent(EVENT_NAME.PDP_VISITED, {
      [EVENT_ATTRIBUTE.COURSE_ID]: course.parentCourseId || course.courseId,
      [EVENT_ATTRIBUTE.COURSE_NAME]: course.courseTitle,
      [EVENT_ATTRIBUTE.COURSE_TYPE_ID]: course.courseTypeId,
      [EVENT_ATTRIBUTE.AUTOPAY_ENABLE]: course.autoPayEnabled ? 1 : 0,
    });
  }, [isAuthenticated, trackMoengageEvent, course]);

  React.useEffect(() => {
    if (!isOnlineEnabled && !isOfflineEnabled) return;
    if (!course.courseId || !courseLanguageId) return;

    setCoursePackages([]);

    const isOfflineCourse = courseMode === "offline" ? 1 : 0;
    const courseOfflineCentreId = isOfflineCourse ? offlineCentreId : undefined;

    // Fetch course packages for selected course and language
    getCoursePackages({
      courseId: course.parentCourseId || course.courseId,
      languageId: courseLanguageId,
      isOfflineCourse,
      offlineCentreId: courseOfflineCentreId || undefined,
      action: 1,
    }).then(({ data }) => {
      if (!data) return;

      const coursePackages = data.packages.map((coursePackage) => ({
        ...coursePackage,
        packageValidityInDays: dayjs()
          .add(coursePackage.packageValidity, "months")
          .diff(dayjs(), "days"),
      }));

      // Set course packages atom
      setCoursePackages(coursePackages);

      let defaultSelectedPackage = coursePackages.find(
        (coursePackage) => coursePackage.bestSelling
      );

      // If no best selling package found then select first package
      defaultSelectedPackage = defaultSelectedPackage || coursePackages[0];
      if (!defaultSelectedPackage) return;

      // Select default package
      methods.setValue("packageId", defaultSelectedPackage.packageId);
    });
  }, [
    courseMode,
    courseLanguageId,
    offlineCentreId,
    course.courseId,
    course.parentCourseId,
    isOnlineEnabled,
    isOfflineEnabled,
    methods,
    setCoursePackages,
  ]);

  return (
    <Form {...methods}>
      <div
        id="course-form-wrapper"
        className="group/course-form"
        aria-disabled={userCoursePurchased ? "yes" : "no"}
      >
        {children}
      </div>
    </Form>
  );
}

export default CourseForm;
